import { PromotionLine } from "./types"

/**
 * One aggregated per-unit rate chip for a promotion, e.g. "Scan $1.30–$1.52/unit".
 * Amounts are kept in cents so range comparison and dedup never hit float noise.
 */
export interface RateChip {
  kind: string
  min_cents: number
  max_cents: number
}

const KIND_ORDER = ["scan", "billback", "off-invoice", "club scan"]

/**
 * Aggregate the per-line unit rates into one chip per rate kind (min–max range
 * across lines). Imported promotions rarely set the header discount fields, so
 * these chips are usually the only real deal terms shown on the edit form.
 */
export function summarize_line_rates(lines: PromotionLine[]): RateChip[] {
  const by_kind = new Map<string, number[]>()
  for (const line of lines) {
    for (const rate of line.unit_rates ?? []) {
      if (!Number.isFinite(rate.amount) || rate.amount <= 0) continue
      const cents = Math.round(rate.amount * 100)
      const bucket = by_kind.get(rate.kind) ?? []
      bucket.push(cents)
      by_kind.set(rate.kind, bucket)
    }
  }
  return [...by_kind.entries()]
    .map(([kind, cents]) => ({ kind, min_cents: Math.min(...cents), max_cents: Math.max(...cents) }))
    .sort((a, b) => {
      const [ai, bi] = [KIND_ORDER.indexOf(a.kind), KIND_ORDER.indexOf(b.kind)]
      return (ai === -1 ? KIND_ORDER.length : ai) - (bi === -1 ? KIND_ORDER.length : bi) || a.kind.localeCompare(b.kind)
    })
}

/** The contract charge percentage range across lines, as [min, max] fractions, or null. */
export function summarize_charge_pct(lines: PromotionLine[]): [number, number] | null {
  const pcts = lines
    .map(line => line.charge_rate_pct)
    .filter((p): p is number => typeof p === "number" && Number.isFinite(p) && p > 0)
  if (pcts.length === 0) return null
  return [Math.min(...pcts), Math.max(...pcts)]
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/** "Scan $1.30–$1.52/unit" or "Billback $0.42/unit". */
export function format_rate_chip(chip: RateChip): string {
  const kind = chip.kind.charAt(0).toUpperCase() + chip.kind.slice(1)
  const range = chip.min_cents === chip.max_cents ? dollars(chip.min_cents) : `${dollars(chip.min_cents)}–${dollars(chip.max_cents)}`
  return `${kind} ${range}/unit`
}

/** "10.14% of price" or "10.14%–11.5% of price". */
export function format_charge_pct([min, max]: [number, number]): string {
  const pct = (p: number) => `${+(p * 100).toFixed(2)}%`
  return `${min === max ? pct(min) : `${pct(min)}–${pct(max)}`} of price`
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function line(unit_rates: PromotionLine["unit_rates"], charge_rate_pct?: number | null): PromotionLine {
    return { id: "l", promotion_id: "p", unit_rates, charge_rate_pct }
  }

  describe("summarize_line_rates", () => {
    it("aggregates each kind into a min–max range across lines", () => {
      const chips = summarize_line_rates([
        line([{ kind: "scan", amount: 1.3 }]),
        line([{ kind: "scan", amount: 1.52 }]),
        line([{ kind: "scan", amount: 1.3 }, { kind: "off-invoice", amount: 0.5 }]),
      ])
      expect(chips).toEqual([
        { kind: "scan", min_cents: 130, max_cents: 152 },
        { kind: "off-invoice", min_cents: 50, max_cents: 50 },
      ])
      expect(format_rate_chip(chips[0])).toBe("Scan $1.30–$1.52/unit")
      expect(format_rate_chip(chips[1])).toBe("Off-invoice $0.50/unit")
    })

    it("orders known kinds stably and skips zero/invalid amounts", () => {
      const chips = summarize_line_rates([
        line([{ kind: "off-invoice", amount: 0.5 }, { kind: "billback", amount: 0.42 }, { kind: "scan", amount: 0 }]),
      ])
      expect(chips.map(c => c.kind)).toEqual(["billback", "off-invoice"])
    })

    it("handles lines without rates", () => {
      expect(summarize_line_rates([line(undefined), line(null), line([])])).toEqual([])
    })
  })

  describe("summarize_charge_pct", () => {
    it("returns the fraction range and formats it as a percentage", () => {
      expect(summarize_charge_pct([line([], 0.1014), line([], 0.105), line([], null)])).toEqual([0.1014, 0.105])
      expect(format_charge_pct([0.1014, 0.105])).toBe("10.14%–10.5% of price")
      expect(format_charge_pct([0.1014, 0.1014])).toBe("10.14% of price")
      expect(summarize_charge_pct([line([])])).toBeNull()
    })
  })
}
